import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { SidenavService } from '@app/core/services/container/sidenav.service';
import { FieldAutocomplete } from '@app/shared/models/field.interface';
import { Entity } from '@app/shared/models/models.interface';
import { CustomOverlayDescriptor } from '@app/shared/services/overlay/custom-overlay.service';
import { DataTableNewComponent } from '@components/data/data-table-new/data-table-new.component';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { Subject } from 'rxjs';

export type StringTableStrategy<T extends Entity> = (selection: EntitySelectionModel<T>) => string | string[];

@Component({
  selector: 'vfs-input-stringtable',
  templateUrl: './input-stringtable.component.html',
  styleUrls: ['./input-stringtable.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputStringtableComponent)
    }
  ]
})
export class InputStringtableComponent implements OnInit {
  @Input() label: string;
  @Input() tooltip?: string;
  @Input() hint: string;
  @Input() error: string;
  @Input() formControl: FormControl;
  @Input() type: string;
  @Input() placeholder = '';
  @Input() required: boolean;
  @Input() maxLength = 255;
  @Input() autocomplete: FieldAutocomplete = FieldAutocomplete.OFF;
  @Input() autofocus: boolean;
  @Input() ngModel: any;
  @Output() ngModelChange = new EventEmitter<any>();
  @Input() disabledButton = false;
  // DataTable
  @Input() title: string;
  @Input() dataTableTemplate: TemplateRef<DataTableNewComponent<any>>;
  @Input() strategy: StringTableStrategy<any>;
  @Input() selection: EntitySelectionModel<any>;
  @Input() initialSelection: any[];
  @Input() singleSelection = false;

  @Input() closeSidenav = true;
  @Input() cancelAcceptSubject$: Subject<boolean>;

  @Input() disabledSelectButton = false;
  @Input() disabledCancelButton = false;
  @Input() disabled: boolean;
  @Input() customOverlayDescriptor: CustomOverlayDescriptor;

  @Input() hiddenActions = false;

  @Output() overlayOpen = new EventEmitter<void>();
  @Output() overlayClose = new EventEmitter<void>();
  @Output() overlayClear = new EventEmitter<void>();
  @Output() overlayAccept = new EventEmitter<void>();

  @ViewChild(MatInput) matInput: MatInput;

  selectedState: any[];
  value: any;

  constructor(private sidenavService: SidenavService) {}

  ngOnInit() {
    if (!this.ngModel && !this.formControl) {
      console.warn('Input Stringtable needs a ngModel or formControl to work');
    }
    if (this.initialSelection) {
      this.selection.select(...this.initialSelection);
      this.selectedState = this.initialSelection;
      this.setValue();
    } else if (this.ngModel) {
      this.selection.select(...this.ngModel);
      this.selectedState = this.ngModel;
      this.setValue();
    }
  }

  onChange = (rating: number) => {
    this.hasInputValue();
  };

  onTouched = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openSidenav(template: TemplateRef<any>) {
    if (this.customOverlayDescriptor && this.customOverlayDescriptor.customDrawer) {
      this.customOverlayDescriptor.template = template;
      this.customOverlayDescriptor.customDrawer.open();
    } else {
      this.sidenavService.openSidenav(template, !this.singleSelection);
    }
    this.overlayOpen.emit();
  }

  close(): void {
    this.overlayClose.emit();
  }

  cancel() {
    this.selection.clear();

    if (this.selectedState) {
      this.selection.select(...this.selectedState);
    }

    if (this.cancelAcceptSubject$) {
      this.cancelAcceptSubject$.next(false);
    }
  }

  clear() {
    this.selection.clear();
    if (this.formControl) {
      this.formControl.setValue('');
    } else if (this.ngModel) {
      this.ngModel = [];
      this.ngModelChange.emit(this.ngModel);
    }
    this.overlayClear.emit();
  }

  accept() {
    this.selectedState = Object.assign([], this.selection.selected);
    this.setValue();
    if (this.cancelAcceptSubject$) {
      this.cancelAcceptSubject$.next(true);
    }
    this.overlayAccept.emit();
  }

  setValue() {
    if (this.strategy) {
      let value = this.strategy(this.selection);
      this.ngModel && !this.formControl ? this.setNgModel(value) : this.formControl.setValue(value);
    } else {
      let value = this.selection.selected.map(el => el.id);
      this.ngModel && !this.formControl ? this.setNgModel(value) : this.formControl.setValue(value);
    }
  }

  hasInputValue() {
    if (this.value !== undefined && this.value != null) {
      return (
        (this.value instanceof Array && this.value.length > 0) || (typeof this.value === 'string' && this.value !== '')
      );
    }
  }

  private enableClearButton(): boolean {
    if (this.formControl) {
      return !this.formControl.disabled && !this.disabledButton && !this.disabled && this.formControl.value.length > 0;
    } else {
      return !this.disabledButton && !this.disabled && this.ngModel.length > 0;
    }
  }

  public onClearClick(): void {
    if (this.enableClearButton()) {
      this.clear();
    }
  }

  private setNgModel(value: any) {
    this.ngModel = value;
    this.ngModelChange.emit(this.ngModel);
  }

  ngModelIsValid(): boolean {
    return (
      this.matInput && this.matInput.ngControl && (this.matInput.ngControl.valid || this.matInput.ngControl.disabled)
    );
  }
}
